import type { ApiClient } from '@klareospace/pharos-lib-front-utils';
import {
  applyScopeFiltersToBody,
  mapPaginatedResponse,
  setArrayFilter,
  toQueryParams,
} from '@klareospace/pharos-lib-front-utils';
import type {
  PaginatedResponse,
  PaginationParams,
  PharosApiResponse,
  PharosPaginatedResponse,
  SortParams,
  UserStatus,
  UserWithResolvedScopes,
} from '@klareospace/pharos-lib-types';

import type { DriverSortableColumn } from '../types/driver.types';

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchDriversParams extends PaginationParams {
  carrierId?: string;
  carrierIds?: string[];
  siteIds?: string[];
  sort?: SortParams<DriverSortableColumn>;
  spaceId?: string;
  statuses?: UserStatus[];
  text?: string;
}

// ─── API Factory ──────────────────────────────────────────────────────────────

// Create/update go through the unified user stack (POST /invitations, PATCH /users/:id) — this
// API surface keeps only the search + lifecycle endpoints specific to the drivers management pages.
export function createDriverApi(client: ApiClient): {
  activateDriver: (id: string) => Promise<UserWithResolvedScopes>;
  archiveDriver: (id: string) => Promise<UserWithResolvedScopes>;
  deactivateDriver: (id: string) => Promise<UserWithResolvedScopes>;
  searchDrivers: (params: SearchDriversParams) => Promise<PaginatedResponse<UserWithResolvedScopes>>;
} {
  return {
    activateDriver: async (id: string): Promise<UserWithResolvedScopes> => {
      const response = await client.execute<PharosApiResponse<UserWithResolvedScopes>>(() =>
        client.instance.post(`/drivers/${id}/activate`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    archiveDriver: async (id: string): Promise<UserWithResolvedScopes> => {
      const response = await client.execute<PharosApiResponse<UserWithResolvedScopes>>(() =>
        client.instance.post(`/drivers/${id}/archive`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    deactivateDriver: async (id: string): Promise<UserWithResolvedScopes> => {
      const response = await client.execute<PharosApiResponse<UserWithResolvedScopes>>(() =>
        client.instance.post(`/drivers/${id}/deactivate`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchDrivers: async (params: SearchDriversParams): Promise<PaginatedResponse<UserWithResolvedScopes>> => {
      const body = toSearchBody(params);
      const query = toQueryParams(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<UserWithResolvedScopes>>>(() =>
        client.instance.post('/drivers/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build the POST body for /drivers/search from filter params.
 * Only includes fields that have been explicitly set.
 */
function toSearchBody(params: SearchDriversParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedSearch = params.text?.trim();

  if (trimmedSearch) body.text = trimmedSearch;
  if (params.carrierId) body.carrierId = params.carrierId;
  applyScopeFiltersToBody(body, params);
  setArrayFilter(body, 'carrierIds', params.carrierIds);
  setArrayFilter(body, 'statuses', params.statuses);

  return body;
}
